import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Invoice, InvoiceItem, Client } from '../types/invoice';

interface CreateInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (invoice: Invoice) => void;
  clients: Client[];
  onAddClient: (client: Client) => void;
  stripeAccountId: string | null;
  currentUserId: string;
  editingInvoice?: Invoice | null;
}

export default function CreateInvoiceModal({ isOpen, onClose, onSave, clients, onAddClient, stripeAccountId, currentUserId, editingInvoice }: CreateInvoiceModalProps) {
  const [step, setStep] = useState<'business' | 'client' | 'items' | 'details'>('business');
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  
  // Business Info
  const [businessName, setBusinessName] = useState('');
  const [businessEmail, setBusinessEmail] = useState('');
  const [businessAddress, setBusinessAddress] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  
  // Client Info
  const [selectedClientId, setSelectedClientId] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientCompany, setClientCompany] = useState('');
  
  // Invoice Details
  const [items, setItems] = useState<InvoiceItem[]>([
    { id: '1', description: '', quantity: 1, rate: 0, amount: 0 }
  ]);
  const [issueDate, setIssueDate] = useState(new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState(
    new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  const [taxRate, setTaxRate] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState('Payment is due within 30 days. Late payments may incur additional fees.');
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringFrequency, setRecurringFrequency] = useState<'monthly' | 'quarterly' | 'yearly'>('monthly');
  const [recurringEndDate, setRecurringEndDate] = useState('');

  // Load saved business info or editing invoice
  useEffect(() => {
    if (editingInvoice) {
      // Populate form with editing invoice data
      setBusinessName(editingInvoice.businessName);
      setBusinessEmail(editingInvoice.businessEmail);
      setBusinessAddress(editingInvoice.businessAddress || '');
      setBusinessPhone(editingInvoice.businessPhone || '');
      setClientName(editingInvoice.clientName);
      setClientEmail(editingInvoice.clientEmail);
      setClientAddress(editingInvoice.clientAddress || '');
      setClientPhone(editingInvoice.clientPhone || '');
      setItems(editingInvoice.items);
      setIssueDate(editingInvoice.issueDate);
      setDueDate(editingInvoice.dueDate);
      setTaxRate(editingInvoice.taxRate);
      setDiscount(editingInvoice.discount);
      setNotes(editingInvoice.notes || '');
      setTerms(editingInvoice.terms || 'Payment is due within 30 days. Late payments may incur additional fees.');
      setIsRecurring(editingInvoice.isRecurring || false);
      setRecurringFrequency(editingInvoice.recurringFrequency || 'monthly');
      setRecurringEndDate(editingInvoice.recurringEndDate || '');
    } else {
      // Load saved business info for new invoice
      const savedBusiness = localStorage.getItem('businessInfo');
      if (savedBusiness) {
        const business = JSON.parse(savedBusiness);
        setBusinessName(business.name || '');
        setBusinessEmail(business.email || '');
        setBusinessAddress(business.address || '');
        setBusinessPhone(business.phone || '');
      }
    }
  }, [editingInvoice]);

  // Calculate totals
  const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
  const taxAmount = (subtotal * taxRate) / 100;
  const total = subtotal + taxAmount - discount;

  const addItem = () => {
    setItems([...items, { 
      id: uuidv4(), 
      description: '', 
      quantity: 1, 
      rate: 0, 
      amount: 0 
    }]);
  };

  const removeItem = (id: string) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };

  const updateItem = (id: string, field: keyof InvoiceItem, value: any) => {
    setItems(items.map(item => {
      if (item.id === id) {
        const updated = { ...item, [field]: value };
        if (field === 'quantity' || field === 'rate') {
          updated.amount = updated.quantity * updated.rate;
        }
        return updated;
      }
      return item;
    }));
  };

  const handleClientSelect = (clientId: string) => {
    setSelectedClientId(clientId);
    if (clientId === 'new') {
      setShowNewClientForm(true);
      setClientName('');
      setClientEmail('');
      setClientAddress('');
      setClientPhone('');
      setClientCompany('');
    } else if (clientId) {
      const client = clients.find(c => c.id === clientId);
      if (client) {
        setClientName(client.name);
        setClientEmail(client.email);
        setClientAddress(client.address || '');
        setClientPhone(client.phone || '');
        setClientCompany(client.company || '');
        setShowNewClientForm(false);
      }
    }
  };

  const saveNewClient = () => {
    const newClient: Client = {
      id: uuidv4(),
      name: clientName,
      email: clientEmail,
      phone: clientPhone,
      address: clientAddress,
      company: clientCompany,
      totalInvoiced: 0,
      totalPaid: 0,
      invoiceCount: 0,
      userId: currentUserId,
      createdAt: new Date().toISOString(),
    };
    onAddClient(newClient);
    setSelectedClientId(newClient.id);
    setShowNewClientForm(false);
  };

  const handleSaveBusiness = () => {
    const businessInfo = {
      name: businessName,
      email: businessEmail,
      address: businessAddress,
      phone: businessPhone,
    };
    localStorage.setItem('businessInfo', JSON.stringify(businessInfo));
    setStep('client');
  };

  const handleSaveInvoice = (sendImmediately: boolean) => {
    // Use existing ID and invoice number if editing, otherwise generate new ones with UUID
    const invoiceId = editingInvoice?.id || uuidv4();
    const invoiceNumber = editingInvoice?.invoiceNumber || `INV-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`;
    
    // Calculate next recurring date if applicable
    let nextRecurringDate;
    if (isRecurring) {
      const daysToAdd = recurringFrequency === 'monthly' ? 30 : 
                       recurringFrequency === 'quarterly' ? 90 : 365;
      nextRecurringDate = new Date(Date.now() + daysToAdd * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    }
    
    const invoice: Invoice = {
      id: invoiceId,
      invoiceNumber,
      clientName,
      clientEmail,
      clientAddress,
      clientPhone,
      businessName,
      businessEmail,
      businessAddress,
      businessPhone,
      items,
      subtotal,
      tax: taxAmount,
      taxRate,
      discount,
      total,
      status: sendImmediately ? 'sent' : 'draft',
      issueDate,
      dueDate,
      notes,
      terms,
      userId: currentUserId,
      createdAt: editingInvoice?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isRecurring,
      recurringFrequency: isRecurring ? recurringFrequency : undefined,
      nextRecurringDate: isRecurring ? nextRecurringDate : undefined,
      recurringEndDate: isRecurring && recurringEndDate ? recurringEndDate : undefined,
      stripePaymentLinkId: editingInvoice?.stripePaymentLinkId,
    };

    onSave(invoice);
    
    if (sendImmediately) {
      // Send invoice - will open email client and show sharing options
      sendInvoiceEmail(invoice);
    } else {
      alert('Invoice saved as draft! You can send it from the Invoices list.');
    }
    
    // Reset form
    resetForm();
    onClose();
  };

  const sendInvoiceEmail = async (invoice: Invoice) => {
    try {
      if (!stripeAccountId) {
        alert('Please connect your Stripe account before sending invoices');
        return;
      }
      
      const response = await fetch('/.netlify/functions/send-invoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoice, stripeAccountId }),
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Store payment link ID in invoice
        invoice.stripePaymentLinkId = data.paymentLinkId;
        
        // Create email template
        const emailSubject = `Invoice ${invoice.invoiceNumber} from ${invoice.businessName}`;
        const emailBody = `Hello ${invoice.clientName},

Please find your invoice below:

Invoice Number: ${invoice.invoiceNumber}
Amount Due: $${invoice.total.toFixed(2)}
Due Date: ${new Date(invoice.dueDate).toLocaleDateString()}

Click here to view and pay your invoice:
${data.paymentLink}

Thank you for your business!

${invoice.businessName}
${invoice.businessEmail}`;

        // Show modal with copy options
        const modal = document.createElement('div');
        modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4';
        modal.innerHTML = `
          <div class="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6">
            <h3 class="text-2xl font-bold text-gray-900 mb-4">📧 Invoice Ready to Send</h3>
            
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-2">Payment Link</label>
                <div class="flex gap-2">
                  <input type="text" readonly value="${data.paymentLink}" 
                    class="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm" />
                  <button onclick="navigator.clipboard.writeText('${data.paymentLink}'); this.textContent='✓ Copied!'" 
                    class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold whitespace-nowrap">
                    Copy Link
                  </button>
                </div>
              </div>
              
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-2">Email Template (copy & paste into your email)</label>
                <textarea readonly class="w-full h-64 px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono" 
                  onclick="this.select()">${emailBody}</textarea>
              </div>
              
              <div class="flex gap-3">
                <button onclick="navigator.clipboard.writeText(\`Subject: ${emailSubject}\\n\\n${emailBody.replace(/\n/g, '\\n')}\`); alert('Email template copied! Paste into your email client.');" 
                  class="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold">
                  📋 Copy Full Email
                </button>
                <button onclick="window.open('sms:${invoice.clientPhone || ''}?&body=' + encodeURIComponent('${emailBody.substring(0, 100)}... ${data.paymentLink}'))" 
                  class="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold">
                  💬 Send via SMS
                </button>
              </div>
              
              <button onclick="this.closest('.fixed').remove()" 
                class="w-full px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold">
                Done
              </button>
            </div>
          </div>
        `;
        document.body.appendChild(modal);
      } else {
        const errorData = await response.json();
        alert('Failed to create invoice: ' + (errorData.message || errorData.error));
      }
    } catch (error) {
      console.error('Error sending invoice:', error);
      alert('Failed to prepare invoice email. Please try again.');
    }
  };

  const resetForm = () => {
    setStep('business');
    setItems([{ id: '1', description: '', quantity: 1, rate: 0, amount: 0 }]);
    setSelectedClientId('');
    setClientName('');
    setClientEmail('');
    setClientAddress('');
    setClientPhone('');
    setClientCompany('');
    setTaxRate(0);
    setDiscount(0);
    setNotes('');
    setShowNewClientForm(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-xl">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">Create New Invoice</h2>
              <p className="text-blue-100 mt-1">
                {step === 'business' && 'Step 1: Your Business Information'}
                {step === 'client' && 'Step 2: Client Information'}
                {step === 'items' && 'Step 3: Invoice Items'}
                {step === 'details' && 'Step 4: Review & Send'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-3xl font-bold"
            >
              ×
            </button>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4 flex gap-2">
            {['business', 'client', 'items', 'details'].map((s, idx) => (
              <div
                key={s}
                className={`flex-1 h-2 rounded-full ${
                  step === s ? 'bg-white' : 
                  ['business', 'client', 'items', 'details'].indexOf(step) > idx ? 'bg-blue-300' : 'bg-blue-400/30'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Business Info */}
          {step === 'business' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800">
                  💡 This information will appear on your invoices and be saved for future use.
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Business Name *
                </label>
                <input
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Your Business Name LLC"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Business Email *
                </label>
                <input
                  type="email"
                  value={businessEmail}
                  onChange={(e) => setBusinessEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="billing@yourbusiness.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Business Address
                </label>
                <textarea
                  value={businessAddress}
                  onChange={(e) => setBusinessAddress(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={2}
                  placeholder="123 Business St, Suite 100, City, State 12345"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Business Phone
                </label>
                <input
                  type="tel"
                  value={businessPhone}
                  onChange={(e) => setBusinessPhone(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={onClose}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveBusiness}
                  disabled={!businessName || !businessEmail}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next: Client Info →
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Client Info */}
          {step === 'client' && (
            <div className="space-y-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-purple-800">
                  💡 Select an existing client or add a new one. Client info will be saved for future invoices.
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Select Client
                </label>
                <select
                  value={selectedClientId}
                  onChange={(e) => handleClientSelect(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Choose a client...</option>
                  <option value="new">+ Add New Client</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name} ({client.email})
                    </option>
                  ))}
                </select>
              </div>

              {(showNewClientForm || selectedClientId) && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Client Name *
                    </label>
                    <input
                      type="text"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="John Doe"
                      readOnly={!showNewClientForm && selectedClientId !== 'new'}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Client Email *
                    </label>
                    <input
                      type="email"
                      value={clientEmail}
                      onChange={(e) => setClientEmail(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="client@example.com"
                      readOnly={!showNewClientForm && selectedClientId !== 'new'}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Company Name
                    </label>
                    <input
                      type="text"
                      value={clientCompany}
                      onChange={(e) => setClientCompany(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Acme Corp"
                      readOnly={!showNewClientForm && selectedClientId !== 'new'}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Client Address
                    </label>
                    <textarea
                      value={clientAddress}
                      onChange={(e) => setClientAddress(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows={2}
                      placeholder="456 Client Ave, City, State 67890"
                      readOnly={!showNewClientForm && selectedClientId !== 'new'}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Client Phone
                    </label>
                    <input
                      type="tel"
                      value={clientPhone}
                      onChange={(e) => setClientPhone(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="(555) 987-6543"
                      readOnly={!showNewClientForm && selectedClientId !== 'new'}
                    />
                  </div>

                  {showNewClientForm && (
                    <button
                      onClick={saveNewClient}
                      disabled={!clientName || !clientEmail}
                      className="w-full px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      ✓ Save Client
                    </button>
                  )}
                </>
              )}

              <div className="flex justify-between gap-3 pt-4">
                <button
                  onClick={() => setStep('business')}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  ← Back
                </button>
                <button
                  onClick={() => setStep('items')}
                  disabled={!clientName || !clientEmail}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next: Add Items →
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Invoice Items */}
          {step === 'items' && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-green-800">
                  💡 Add line items for your services or products. You can add multiple items.
                </p>
              </div>

              {/* Items Table */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Description</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-24">Qty</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-32">Rate</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-32">Amount</th>
                      <th className="px-4 py-3 w-12"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {items.map((item, index) => (
                      <tr key={item.id}>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Service description..."
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            min="0"
                            step="0.01"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            value={item.rate}
                            onChange={(e) => updateItem(item.id, 'rate', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-900">
                            ${item.amount.toFixed(2)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {items.length > 1 && (
                            <button
                              onClick={() => removeItem(item.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              ×
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button
                onClick={addItem}
                className="w-full px-4 py-3 border-2 border-dashed border-gray-300 text-gray-600 rounded-lg font-semibold hover:border-blue-500 hover:text-blue-600 transition-colors"
              >
                + Add Line Item
              </button>

              {/* Totals */}
              <div className="bg-gray-50 rounded-lg p-6 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-semibold text-gray-900">${subtotal.toFixed(2)}</span>
                </div>

                <div className="flex justify-between items-center gap-4">
                  <div className="flex items-center gap-2">
                    <label className="text-gray-600">Tax Rate (%):</label>
                    <input
                      type="number"
                      value={taxRate}
                      onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                      className="w-20 px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      min="0"
                      step="0.1"
                    />
                  </div>
                  <span className="font-semibold text-gray-900">${taxAmount.toFixed(2)}</span>
                </div>

                <div className="flex justify-between items-center gap-4">
                  <div className="flex items-center gap-2">
                    <label className="text-gray-600">Discount ($):</label>
                    <input
                      type="number"
                      value={discount}
                      onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                      className="w-24 px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <span className="font-semibold text-red-600">-${discount.toFixed(2)}</span>
                </div>

                <div className="border-t border-gray-300 pt-3 flex justify-between items-center">
                  <span className="text-lg font-bold text-gray-900">Total:</span>
                  <span className="text-2xl font-bold text-blue-600">${total.toFixed(2)}</span>
                </div>
              </div>

              <div className="flex justify-between gap-3 pt-4">
                <button
                  onClick={() => setStep('client')}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  ← Back
                </button>
                <button
                  onClick={() => setStep('details')}
                  disabled={items.every(item => !item.description || item.amount === 0)}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next: Review →
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Review & Details */}
          {step === 'details' && (
            <div className="space-y-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  💡 Review your invoice and choose to save as draft or send immediately to your client.
                </p>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Issue Date
                  </label>
                  <input
                    type="date"
                    value={issueDate}
                    onChange={(e) => setIssueDate(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Due Date
                  </label>
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Notes (visible to client)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Thank you for your business!"
                />
              </div>

              {/* Terms */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Payment Terms
                </label>
                <textarea
                  value={terms}
                  onChange={(e) => setTerms(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={2}
                />
              </div>

              {/* Recurring Invoice Option */}
              <div className="border-2 border-purple-200 rounded-lg p-4 bg-purple-50">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="isRecurring"
                    checked={isRecurring}
                    onChange={(e) => setIsRecurring(e.target.checked)}
                    className="mt-1 w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                  />
                  <div className="flex-1">
                    <label htmlFor="isRecurring" className="block font-semibold text-gray-900 mb-1 cursor-pointer">
                      🔄 Make this a recurring invoice
                    </label>
                    <p className="text-sm text-gray-600 mb-3">
                      Automatically generate invoices on a schedule
                    </p>
                    
                    {isRecurring && (
                      <div className="space-y-3 mt-3">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Frequency
                          </label>
                          <select
                            value={recurringFrequency}
                            onChange={(e) => setRecurringFrequency(e.target.value as any)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          >
                            <option value="monthly">Monthly</option>
                            <option value="quarterly">Quarterly (every 3 months)</option>
                            <option value="yearly">Yearly</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            End Date (optional)
                          </label>
                          <input
                            type="date"
                            value={recurringEndDate}
                            onChange={(e) => setRecurringEndDate(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            placeholder="Leave empty for no end date"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Leave empty to continue indefinitely
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="border-2 border-gray-200 rounded-lg p-6 bg-white">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Invoice Preview</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">From:</span>
                    <span className="font-semibold text-right">{businessName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">To:</span>
                    <span className="font-semibold text-right">{clientName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-semibold">{clientEmail}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Amount:</span>
                    <span className="text-xl font-bold text-blue-600">${total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Due Date:</span>
                    <span className="font-semibold">{new Date(dueDate).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row justify-between gap-3 pt-4">
                <button
                  onClick={() => setStep('items')}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  ← Back
                </button>
                <div className="flex gap-3">
                  <button
                    onClick={() => handleSaveInvoice(false)}
                    className="flex-1 sm:flex-none px-6 py-3 border-2 border-blue-600 text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
                  >
                    Save as Draft
                  </button>
                  <button
                    onClick={() => handleSaveInvoice(true)}
                    className="flex-1 sm:flex-none px-6 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-lg font-semibold hover:from-green-700 hover:to-blue-700 transition-colors shadow-lg"
                  >
                    💌 Save & Send Email
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
